import { fetchAPI } from "./utils";
import { MessageResponse } from "../types/types";

import {
    CreateSoundLibraryAIParams,
    UpdateSoundLibraryAIParams,
    DeleteSoundLibraryAIParams,
    GetSoundLibraryAIParams,
    CreateSoundLibraryAITypeParams,
    UpdateSoundLibraryAITypeParams,
    DeleteSoundLibraryAITypeParams,
    CreateSoundLibraryAICategoryParams,
    UpdateSoundLibraryAICategoryParams,
    DeleteSoundLibraryAICategoryParams,
} from "../types/requestInputs";

import {
    SoundLibraryAITypeResponse,
    SoundLibraryAICategoryResponse,
    SoundLibraryAIResponse,
} from "../types/requestsOutputs";

// === Sound Library ===

export const getSoundLibraryAI = async ({
    token,
    language,
    includeRewind = false,
    searchQuery = "",
    typeId,
    categoryId,
    page = 1,
    limit = 10,
}: GetSoundLibraryAIParams): Promise<{
    items: SoundLibraryAIResponse[];
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
}> => {
    return fetchAPI<{
        items: SoundLibraryAIResponse[];
        total: number;
        totalPages: number;
        currentPage: number;
        limit: number;
    }>(
        "/get-sound-library-ai",
        "POST",
        {
            includeRewind,
            searchQuery,
            typeId,
            categoryId,
            page,
            limit,
        },
        token,
        language,
    );
};

export const createSoundLibraryAI = async ({ token, data }: CreateSoundLibraryAIParams): Promise<MessageResponse> => {
    return await fetchAPI<MessageResponse>("/create-sound-library-ai", "POST", data, token);
};

export const updateSoundLibraryAI = async ({
    token,
    soundId,
    data,
}: UpdateSoundLibraryAIParams): Promise<MessageResponse> => {
    return await fetchAPI<MessageResponse>("/update-sound-library-ai", "POST", { soundId, data }, token);
};

export const deleteSoundLibraryAI = async ({
    token,
    soundId,
}: DeleteSoundLibraryAIParams): Promise<MessageResponse> => {
    return await fetchAPI<MessageResponse>("/delete-sound-library-ai", "POST", { soundId }, token);
};

// === Types ===

export const createSoundLibraryAIType = async ({
    token,
    data,
}: CreateSoundLibraryAITypeParams): Promise<SoundLibraryAITypeResponse> => {
    return await fetchAPI<SoundLibraryAITypeResponse>("/create-sound-library-ai-type", "POST", data, token);
};

export const updateSoundLibraryAIType = async ({
    token,
    id,
    data,
}: UpdateSoundLibraryAITypeParams): Promise<SoundLibraryAITypeResponse> => {
    return await fetchAPI<SoundLibraryAITypeResponse>("/update-sound-library-ai-type", "POST", { id, data }, token);
};

export const deleteSoundLibraryAIType = async ({
    token,
    id,
}: DeleteSoundLibraryAITypeParams): Promise<MessageResponse> => {
    return await fetchAPI<MessageResponse>("/delete-sound-library-ai-type", "POST", { id }, token);
};

export const getSoundLibraryAITypes = async ({
    token,
    language,
}: {
    token: string;
    language: string;
}): Promise<SoundLibraryAITypeResponse[]> => {
    return await fetchAPI<SoundLibraryAITypeResponse[]>("/get-sound-library-ai-types", "POST", {}, token, language);
};

// === Categories ===

export const createSoundLibraryAICategory = async ({
    token,
    data,
}: CreateSoundLibraryAICategoryParams): Promise<SoundLibraryAICategoryResponse> => {
    return await fetchAPI<SoundLibraryAICategoryResponse>("/create-sound-library-ai-category", "POST", data, token);
};

export const updateSoundLibraryAICategory = async ({
    token,
    id,
    data,
}: UpdateSoundLibraryAICategoryParams): Promise<SoundLibraryAICategoryResponse> => {
    return await fetchAPI<SoundLibraryAICategoryResponse>(
        "/update-sound-library-ai-category",
        "POST",
        { id, data },
        token,
    );
};

export const deleteSoundLibraryAICategory = async ({
    token,
    id,
}: DeleteSoundLibraryAICategoryParams): Promise<MessageResponse> => {
    return await fetchAPI<MessageResponse>("/delete-sound-library-ai-category", "POST", { id }, token);
};

export const getAllSoundLibraryAICategories = async ({
    token,
    language,
}: {
    token: string;
    language: string;
}): Promise<SoundLibraryAICategoryResponse[]> => {
    return await fetchAPI<SoundLibraryAICategoryResponse[]>(
        "/get-sound-library-ai-categories",
        "POST",
        {},
        token,
        language,
    );
};

export const getFilteredSoundLibraryAICategories = async ({
    token,
    language,
    includeRewind = false,
    searchQuery = "",
    typeId,
}: {
    token: string;
    language: string;
    includeRewind?: boolean;
    searchQuery?: string;
    typeId?: string | null;
}): Promise<SoundLibraryAICategoryResponse[]> => {
    return await fetchAPI<SoundLibraryAICategoryResponse[]>(
        "/get-filtered-sound-library-ai-categories",
        "POST",
        {
            includeRewind,
            searchQuery,
            typeId,
        },
        token,
        language,
    );
};
